import { BehaviorSubject } from 'rxjs';
import { addError, clearError } from './Error';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import {
  createProductMapData,
  deleteProductMapData,
  listProductMaps,
  listProductMapsByRpmAcctProductId,
  listProductMapsBySFDCProductId,
  updateProductMapData,
  listUnmappedProducts
} from '../Api/ProductMap/ProductMapApi';

export enum ProductFamily {
  IT_OPERATIONS = 'IT Operations',
  DATA_CENTER_AND_CLOUD = 'Data Center and Cloud',
  COMMUNICATIONS = 'Communications',
  BUSINESS_CONTINUITY = 'Business Continuity',
  INTERNET_OF_THINGS = 'Internet of Things',
  NETWORKING = 'Networking',
  BUSINESS_PROCESS_OPTIMIZATION = 'Business Process Optimization',
  HARDWARE_AND_DEVICES = 'Hardware and Devices',
  CUSTOMER_EXPERIENCE = 'Customer Experience',
  CONTENT_AND_DATA_MANAGEMENT = 'Content and Data Management',
  SECURITY = 'Security'
}

export interface ProductMap {
  id: string;
  productFamily: ProductFamily;
  productId: string;
  productName: string;
  productSfId: string;
  rpmProductId: number;
  rpmProductName: string;
  rpmProductSupplierId: number;
  rpmProductSupplierName: string;
  totalCount: string;
  note: string;
  reviewRequired: boolean;
}

export interface UnmappedProduct {
  rpmProductId: number;
  rpmProductName: string;
  rpmProductSupplierId: number;
  rpmProductSupplierName: string;
  latestRunYearMonth: string;
}

const productMapsSubject = new BehaviorSubject<PaginatedResponse<ProductMap>>({} as PaginatedResponse<ProductMap>);
const unmappedProductSubject = new BehaviorSubject<PaginatedResponse<UnmappedProduct>>(
  {} as PaginatedResponse<UnmappedProduct>
);
const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
const inboundFetchNumberUnmappedSubject = new BehaviorSubject<number>(0);
const productMapsLoadingSubject = new BehaviorSubject<boolean>(false);
const unmappedProductsLoadingSubject = new BehaviorSubject<boolean>(false);
const productMapsBySFDCProductIdLoadingSubject = new BehaviorSubject<boolean>(false);
const productMapsBySFDCProductIdSubject = new BehaviorSubject<ProductMap[]>([] as ProductMap[]);
const productMapsByRpmAcctProductIdLoadingSubject = new BehaviorSubject<boolean>(false);
const productMapsByRpmAcctProductIdSubject = new BehaviorSubject<ProductMap[]>([] as ProductMap[]);

export const getProductMapsSubject = (): BehaviorSubject<PaginatedResponse<ProductMap>> => productMapsSubject;
export const getUnmappedProductsSubject = (): BehaviorSubject<PaginatedResponse<UnmappedProduct>> =>
  unmappedProductSubject;
export const getProductMapsLoadingSubject = (): BehaviorSubject<boolean> => productMapsLoadingSubject;
export const getUnmappedProductsLoadingSubject = (): BehaviorSubject<boolean> => unmappedProductsLoadingSubject;
export const getProductMapsBySFDCProductIdLoadingSubject = (): BehaviorSubject<boolean> =>
  productMapsBySFDCProductIdLoadingSubject;
export const getProductMapsBySFDCProductIdSubject = (): BehaviorSubject<ProductMap[]> =>
  productMapsBySFDCProductIdSubject;
export const getProductMapsByRpmAcctProductIdLoadingSubject = (): BehaviorSubject<boolean> =>
  productMapsByRpmAcctProductIdLoadingSubject;
export const getProductMapsByRpmAcctProductIdSubject = (): BehaviorSubject<ProductMap[]> =>
  productMapsByRpmAcctProductIdSubject;

export const createProductMap = async (productMap: Partial<ProductMap>) => {
  const productMapData: ProductMap = { ...({} as ProductMap), ...productMap };

  const { data, error } = await createProductMapData(productMapData);

  if (data) {
    clearError('product-map');
    fetchProductMaps();
  }
  if (error) addError('product-map', error.message);

  return { data, error };
};

export const fetchProductMaps = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  inboundFetchNumberSubject.next(newFetchNumber);
  if (productMapsSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  productMapsLoadingSubject.next(true);

  const { data, error } = await listProductMaps(filters);
  const currentFetchNumber = inboundFetchNumberSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError('product-map');
    productMapsSubject.next(data);
  }
  if (error) addError('product-map', error.message);

  newFetchNumber === currentFetchNumber && productMapsLoadingSubject.next(false);
};

export const fetchUnmappedProductMaps = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberUnmappedSubject.getValue() + 1;
  inboundFetchNumberUnmappedSubject.next(newFetchNumber);
  if (unmappedProductSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  unmappedProductsLoadingSubject.next(true);

  const { data, error } = await listUnmappedProducts(filters);
  const currentFetchNumber = inboundFetchNumberUnmappedSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError('product-map');
    unmappedProductSubject.next(data);
  }
  if (error) addError('product-map', error.message);

  newFetchNumber === currentFetchNumber && unmappedProductsLoadingSubject.next(false);
};

export const updateProductMap = async (update: ProductMap) => {
  const { data, error } = await updateProductMapData(update);
  if (data) {
    clearError('product-map');
    fetchProductMaps();
  }
  if (error) addError('product-map', error.message);

  return { data, error };
};

export const deleteProductMap = async (id: string) => {
  const { data, error } = await deleteProductMapData(id);
  if (data) {
    clearError('product-map');
    productMapsSubject.next({
      ...productMapsSubject.getValue(),
      data: productMapsSubject.getValue().data.filter((productMap) => productMap.id !== id)
    });
  }
  if (error) addError('product-map', error.message);
};

export const fetchProductMapsByRpmAcctProductId = async (id: string): Promise<ProductMap[] | undefined> => {
  productMapsByRpmAcctProductIdLoadingSubject.next(true);
  const { data, error } = await listProductMapsByRpmAcctProductId(id);
  productMapsByRpmAcctProductIdLoadingSubject.next(false);

  if (error) addError('product-map', error.message);

  if (data) {
    productMapsByRpmAcctProductIdSubject.next(data.data);
    return data.data;
  }
};

export const fetchProductMapsBySFDCProductId = async (id: string): Promise<ProductMap[] | undefined> => {
  productMapsBySFDCProductIdLoadingSubject.next(true);
  const { data, error } = await listProductMapsBySFDCProductId(id);
  productMapsBySFDCProductIdLoadingSubject.next(false);

  if (error) addError('product-map', error.message);

  if (data) {
    productMapsBySFDCProductIdSubject.next(data.data);
    return data.data;
  }
};
